import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { useDialogContext } from '../../contexts/DialogContext';
import { useCustomProvidedBuildsQueue } from '../../hooks/api/organization/useCustomProvidedBuildsQueue';
import { useUpdateCustomProvidedBuildsQueue } from '../../hooks/api/organization/useUpdateCustomProvidedBuildsQueue';
import useToggle from '../../hooks/useToggle';
import Accordion from '../generic/Accordion';
import Button from '../generic/Button';
import Card from '../generic/Card/Card';
import LabeledCheckbox from '../generic/Forms/LabeledCheckbox';
import LabeledTextInput from '../generic/Forms/LabeledTextInput';
import Loader from '../generic/Loader';
import Text from '../generic/Text';

type CustomQueueFormProps = {
  customQueue: string;
  onUpdate: (queue: string) => void;
  isUpdating: boolean;
};

const CustomQueueForm = ({ customQueue, onUpdate, isUpdating }: CustomQueueFormProps) => {
  const [newCustomQueue, setNewCustomQueue] = useState(customQueue);
  const [isCustomQueueEnabled, toggleCustomQueueEnabled] = useToggle(customQueue !== '');

  return (
    <>
      <div className="margin-bottom-md">
        <Text.Paragraph>
          Defines the queue where background jobs will be queued. A custom queue is useful for organizations that submit
          large batches of builds because it prevents downgrading performance of the default queue which is shared by
          almost all organizations.
        </Text.Paragraph>
        <Text.Paragraph>
          Please post a message in <code>#team-system</code> before making any changes.
        </Text.Paragraph>
      </div>
      <div className="margin-bottom-md">
        <LabeledCheckbox
          label="Enable Custom Queue"
          isChecked={isCustomQueueEnabled}
          onChange={toggleCustomQueueEnabled}
          isDisabled={isUpdating}
        />
        <LabeledTextInput
          labelVariant="legacy"
          label="Name"
          value={newCustomQueue}
          onChange={setNewCustomQueue}
          isDisabled={!isCustomQueueEnabled || isUpdating}
        />
      </div>
      <div>
        <Button
          onClick={() => onUpdate(isCustomQueueEnabled ? newCustomQueue : '')}
          isDisabled={isUpdating || (isCustomQueueEnabled && !newCustomQueue)}
        >
          Save
        </Button>
      </div>
    </>
  );
};

type Props = {
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
};

const CustomProvidedBuildsQueueCard: VoidFunctionComponent<Props> = ({ isOpen, onToggle }) => {
  const [isSectionOpen, setIsSectionOpen] = useState(isOpen ?? false);
  const handleToggle = () => {
    setIsSectionOpen(!isSectionOpen);
    onToggle?.(isSectionOpen);
  };

  const { data, isLoading: isLoadingQueue } = useCustomProvidedBuildsQueue();
  const { mutate: updateQueue, isPending: isUpdating } = useUpdateCustomProvidedBuildsQueue();
  const { onConfirm } = useDialogContext();

  const handleCustomQueueUpdate = (newCustomQueue: string) => {
    onConfirm({
      title: 'Update the queue for your organization',
      body: (
        <div>
          Are you sure you want to set <code>{newCustomQueue}</code> as the queue for your organization?
        </div>
      ),
      onConfirm: () => updateQueue({ name: newCustomQueue }),
    });
  };

  return (
    <Card isFullWidth padding="medium">
      <Card.Content>
        <Accordion id="custom-provided-builds-queue" isInitiallyOpen={isSectionOpen}>
          <Accordion.Target onClick={handleToggle}>Custom Provided Builds Queue</Accordion.Target>
          <Accordion.Body>
            <Text.H4>Custom Provided Builds Queue</Text.H4>
            {isLoadingQueue ? (
              <Loader />
            ) : (
              <CustomQueueForm
                customQueue={data?.name || ''}
                onUpdate={handleCustomQueueUpdate}
                isUpdating={isUpdating} />
            )}
          </Accordion.Body>
        </Accordion>
      </Card.Content>
    </Card>
  );
};

export default CustomProvidedBuildsQueueCard;
